'use client';

import { useEffect, useState } from 'react';

interface TranslationSafeWrapperProps {
  children: React.ReactNode;
  showWarning?: boolean;
}

export function TranslationSafeWrapper({
  children,
  showWarning = true
}: TranslationSafeWrapperProps) {
  const [translationDetected, setTranslationDetected] = useState(false);

  useEffect(() => {
    const detectTranslation = () => {
      const htmlLang = document.documentElement.lang;
      const browserLang = navigator.language.substring(0, 2);

      // Check if page lang attribute has been changed by translation
      if (htmlLang !== 'en') {
        setTranslationDetected(true);
        return true;
      }

      // Check for common translation service classes/attributes
      const translationIndicators = [
        document.querySelector('font[class*="notranslate"]'),
        document.querySelector('[class*="translated"]'),
        document.querySelector('[data-google-translate]'),
        document.body.classList.contains('translated-ltr'),
        document.body.classList.contains('translated-rtl'),
      ];

      if (translationIndicators.some(indicator => indicator)) {
        setTranslationDetected(true);
        return true;
      }

      return false;
    };

    // Initial check
    detectTranslation();

    // Monitor for translation changes
    const observer = new MutationObserver(() => {
      detectTranslation();
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['lang', 'class'],
      subtree: false
    });

    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['class'],
      subtree: false
    });

    // Also check periodically in case translation happens later
    const intervalId = setInterval(detectTranslation, 2000);

    return () => {
      observer.disconnect();
      clearInterval(intervalId);
    };
  }, []);

  // Force protection of auth elements
  useEffect(() => {
    const protectElements = () => {
      const authElements = document.querySelectorAll(
        'input[type="email"], input[type="text"][id="otp"], form[data-auth-form], button[type="submit"]'
      );

      authElements.forEach(el => {
        el.setAttribute('translate', 'no');
        el.classList.add('notranslate');
        (el as HTMLElement).spellcheck = false;
      });
    };

    protectElements();

    // Re-protect after any DOM changes
    const observer = new MutationObserver(protectElements);
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    return () => observer.disconnect();
  }, []);

  return (
    <div
      className="notranslate"
      translate="no"
      data-auth-protected="true"
    >
      {showWarning && translationDetected && (
        <div className="mb-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
          <div className="flex items-start gap-2">
            <svg
              className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mt-0.5 flex-shrink-0"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
            <div className="flex-1">
              <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                Browser Translation Detected
              </p>
              <p className="text-xs text-yellow-700 dark:text-yellow-300 mt-1">
                Please disable automatic translation for this page to ensure login works correctly.
                You can usually do this by clicking the translation icon in your browser's address bar.
              </p>
            </div>
          </div>
        </div>
      )}
      {children}
    </div>
  );
}